import { createContext, useEffect, useMemo, useState } from "react";
import { CategoryType, ProductType } from "../clothing.types";

interface Props {
  children: React.ReactNode;
  initialActiveCategory: CategoryType;
}
type ModalData = {
  product: ProductType;
};

type StoreContextType = {
  products: ProductType[];
  activeCategory: CategoryType;
  modalData: ModalData | null;
  loading: boolean;
  setModalData: React.Dispatch<React.SetStateAction<ModalData | null>>;
  setActiveCategory: React.Dispatch<
    React.SetStateAction<CategoryType | undefined>
  >;
  updateProduct: (id: number, data: Partial<ProductType>) => void;
};

export const StoreContext = createContext<StoreContextType | null>(null);

const StoreProvider = ({ children, initialActiveCategory }: Props) => {
  const [products, setProducts] = useState<ProductType[]>([]);
  const [activeCategory, setActiveCategory] = useState<CategoryType>(
    initialActiveCategory,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [modalData, setModalData] = useState<ModalData | null>(null);

  /**
   * Fire-and-forget: create an off-screen Image object for every product URL.
   * The browser fetches and caches each image immediately — so by the time the
   * skeleton exits and product cards render, the images are already in cache.
   */
  function preloadImages(products: ProductType[]) {
    products.forEach((p) => {
      if (!p.imgSrc) return;
      const img = new Image();
      img.src = p.imgSrc;
    });
  }

  async function getProducts() {
    try {
      setLoading(true);

      await new Promise((res) => {
        setTimeout(() => {
          const data: ProductType[] = [
            { id: 1, title: "Premium Hoodie Black", price: 79, stock: 600, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 2, title: "Oversized T-Shirt White", price: 39, stock: 600, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 3, title: "Slim Fit Jeans Blue", price: 89, stock: 22, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 4, title: "Leather Jacket Brown", price: 199, stock: 33, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 5, title: "Basic Cap Grey", price: 25, stock: 33, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 6, title: "Sport Sneakers Red", price: 120, stock: 66, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 7, title: "Classic Shirt Navy", price: 55, stock: 66, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 8, title: "Cargo Pants Olive", price: 95, stock: 600, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 9, title: "Winter Coat Black", price: 220, stock: 600, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 10, title: "Denim Shorts Light Blue", price: 49, stock: 600, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 11, title: "Zip Hoodie Beige", price: 85, stock: 2, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 12, title: "Tank Top White", price: 29, stock: 600, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 13, title: "Tracksuit Set Black", price: 150, stock: 1421, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 14, title: "Wool Sweater Cream", price: 99, stock: 632, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
            { id: 15, title: "Minimalist Backpack", price: 110, stock: 600, imgSrc: "https://i.ibb.co/1fd2Zxf9/image-135.png" },
          ];
          // Preload images immediately — before setLoading(false) and before
          // the skeleton exits. Cache will be warm when cards render.
          preloadImages(data);
          setProducts(data);
          res(true);
        }, 1000);
      });
    } finally {
      setLoading(false);
    }
  }

  function updateProduct(id: number, data: Partial<ProductType>) {
    setProducts((prev) =>
      prev.map((product) =>
        product.id === id ? { ...product, ...data } : product,
      ),
    );
  }

  useEffect(() => {
    if (!activeCategory)
      throw new Error("ActiveCategory insite StoreProvider is missing");

    getProducts();

    // api call / filter products
  }, [activeCategory]);

  const value = useMemo(() => {
    return {
      products,
      activeCategory,
      modalData,
      loading,
      setActiveCategory,
      updateProduct,
      setModalData,
    };
  }, [products, activeCategory, loading, modalData]);

  return (
    <StoreContext.Provider value={value as StoreContextType}>
      {children}
    </StoreContext.Provider>
  );
};

export default StoreProvider;
